import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [error, setError] = useState(null);
  const [debug, setDebug] = useState(null);
  // Guard against double-execution (React StrictMode, hot reload, etc.).
  // session_ids are one-time-use — a second call returns 401 "Invalid session".
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const handleCallback = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      const fullUrl = `${API}/auth/google/session`;

      // Surface the URL we are about to call so users can paste it back when something fails.
      setDebug({ apiBase: API, fullUrl, sessionIdPresent: !!sessionId });

      if (!sessionId) {
        setError('No session ID found in the callback URL.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // No withCredentials — the platform edge forces Allow-Origin: *, which is
        // incompatible with credentials. We use JWT in the response body instead.
        const response = await axios.post(
          fullUrl,
          { session_id: sessionId },
          { timeout: 25000, withCredentials: false }
        );

        const { access_token: jwt, user: userData } = response.data || {};
        if (jwt) {
          localStorage.setItem('token', jwt);
          setToken(jwt);
        }
        if (userData) {
          localStorage.setItem('carhippo_user', JSON.stringify(userData));
          localStorage.setItem('carhippo_auth_method', 'google');
          setUser(userData);
        }
        // Clear the session_id so a refresh doesn't try to consume it again.
        try { window.history.replaceState(null, '', '/'); } catch {}
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Google auth error:', err);
        const status = err.response?.status;
        const detail = err.response?.data?.detail
          || (err.code === 'ECONNABORTED' ? 'Request timed out' : null)
          || err.message
          || 'Authentication failed';
        // Include the URL we tried + status so we can spot proxy/routing issues.
        setError(`${detail}${status ? ` · HTTP ${status}` : ''} · ${fullUrl}`);
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="auth-callback">
      <div className="text-center max-w-lg">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-xl">✕</span>
            </div>
            <p className="text-red-400 text-sm mb-3 break-words">{error}</p>
            {debug && (
              <div className="text-[10px] text-gray-500 font-mono bg-white/5 rounded-lg p-3 mb-3 text-left break-all">
                <div>API base: {debug.apiBase}</div>
                <div>URL: {debug.fullUrl}</div>
                <div>session_id: {debug.sessionIdPresent ? 'present' : 'missing'}</div>
              </div>
            )}
            <p className="text-gray-500 text-xs">Redirecting to login in 5s…</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-300 text-sm">Completing Google sign-in…</p>
            <p className="text-gray-500 text-xs mt-1">Please wait</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
